import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useResource } from '../lib/useResource.js';
import { post } from '../lib/api.js';
import { formatDate, formatPhone } from '../lib/format.js';
import { Card, ErrorBox, Field, Modal, PageHeader, useSubmit, useToast } from '../components/ui.js';
import { Tabela } from '../components/Tabela.js';
import { useSession } from '../lib/session.js';

interface Customer {
  id: string;
  name: string;
  phone_e164: string | null;
  tags: string[];
  created_at: string;
  source: string;
}

export function Customers() {
  const { can } = useSession();
  const toast = useToast();
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  const [criando, setCriando] = useState(false);
  const lista = useResource<{ customers: Customer[] }>(
    `/api/customers?term=${encodeURIComponent(query)}`,
  );

  return (
    <>
      <PageHeader
        title="Clientes e veículos"
        description="Busque por nome ou telefone. O telefone é normalizado no cadastro e cadastros diferentes nunca são fundidos automaticamente."
        actions={
          can('customers.write') ? (
            <button type="button" className="primary" onClick={() => setCriando(true)}>
              Novo cliente
            </button>
          ) : null
        }
      />

      <form
        className="toolbar"
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(term);
        }}
      >
        <div className="grow">
          <input
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Nome ou telefone"
            aria-label="Buscar cliente"
          />
        </div>
        <button type="submit">Buscar</button>
        {query ? (
          <button
            type="button"
            className="ghost"
            onClick={() => {
              setTerm('');
              setQuery('');
            }}
          >
            Limpar
          </button>
        ) : null}
      </form>

      <ErrorBox error={lista.error} />

      <Card tight>
        <Tabela
          linhas={lista.data?.customers ?? []}
          chave={(c) => c.id}
          vazio={query ? 'Nenhum cliente encontrado.' : 'Nenhum cliente cadastrado ainda.'}
          vazioDetalhe={query ? null : 'Cadastre o primeiro cliente ou importe a lista em Configurações.'}
          colunas={[
            {
              chave: 'nome',
              rotulo: 'Nome',
              tipo: 'principal',
              render: (c) => <Link to={`/clientes/${c.id}`}>{c.name}</Link>,
            },
            { chave: 'telefone', rotulo: 'Telefone', render: (c) => formatPhone(c.phone_e164) },
            {
              chave: 'etiquetas',
              rotulo: 'Etiquetas',
              ocultarNoCelular: true,
              render: (c) => c.tags.join(', ') || '—',
            },
            {
              chave: 'origem',
              rotulo: 'Origem',
              ocultarNoCelular: true,
              render: (c) => (c.source === 'import' ? 'importado' : c.source),
            },
            { chave: 'cadastro', rotulo: 'Cadastro', render: (c) => formatDate(c.created_at) },
            {
              chave: 'acoes',
              rotulo: '',
              tipo: 'actions',
              render: (c) => (
                <Link className="button small" to={`/clientes/${c.id}`}>
                  Abrir ficha
                </Link>
              ),
            },
          ]}
        />
      </Card>

      {criando ? (
        <NovoCliente
          onClose={() => setCriando(false)}
          onCreated={() => {
            setCriando(false);
            lista.reload();
            toast('Cliente cadastrado.');
          }}
        />
      ) : null}
    </>
  );
}

function NovoCliente({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  const { submit, saving, error } = useSubmit(async () => {
    await post('/api/customers', { name, phone: phone || null, notes: notes || null });
    onCreated();
  });

  return (
    <Modal
      title="Novo cliente"
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" form="form-novo-cliente" className="primary" disabled={saving}>
            {saving ? 'Salvando…' : 'Cadastrar'}
          </button>
        </>
      }
    >
      <form id="form-novo-cliente" onSubmit={submit}>
        <ErrorBox error={error} />
        <Field label="Nome" required>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </Field>
        <Field label="Telefone" hint="Formato brasileiro com DDD. Ex.: (71) 98888-7777">
          <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
        </Field>
        <Field label="Observações">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <p className="hint">
          O cadastro não cria consentimento para lembretes. O aceite é registrado na ficha do cliente.
        </p>
      </form>
    </Modal>
  );
}
